
/*
* @license
* ANALOG Theme (c) Groupthought Themes
*
* This file is included for advanced development by
* Shopify Agencies. Modified versions of the theme
* code are not supported by Shopify or Groupthought.
*
* Add any minor JS changes in assets/custom.js, this
* will make it easier to update the theme in the future.
*
*/
                  
import Alpine from 'alpinejs';
import { motionValue, animate, scroll, springValue, transformValue, styleEffect, inView } from 'motion';
import { isFocusable, focusable } from 'tabbable';

(function() {
    const env = {"NODE_ENV":"production"};
    try {
        if (process) {
            process.env = Object.assign({}, process.env);
            Object.assign(process.env, env);
            return;
        }
    } catch (e) {} // avoid ReferenceError: process is not defined
    globalThis.process = { env:env };
})();

/**
 * Returns the token that follows a directive modifier name.
 *
 * @example
 * getModifierValue(['speed', '60'], 'speed');
 * // => '60'
 */
function getModifierValue(modifiers, modifierName) {
    var _a;
    const modifierIndex = modifiers.indexOf(modifierName);
    if (modifierIndex === -1)
        return null;
    return (_a = modifiers[modifierIndex + 1]) !== null && _a !== void 0 ? _a : null;
}
/**
 * Returns a numeric modifier value when it can be parsed as a finite number.
 *
 * @example
 * getNumberModifierValue(['speed', '-60'], 'speed');
 * // => -60
 */
function getNumberModifierValue(modifiers, modifierName) {
    const rawModifierValue = getModifierValue(modifiers, modifierName);
    if (rawModifierValue == null)
        return null;
    const parsedModifierValue = Number(rawModifierValue);
    if (!Number.isFinite(parsedModifierValue))
        return null;
    return parsedModifierValue;
}

function isReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
function isLowPowerDevice() {
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}

/**
 * Helpers to defer geometry reads until after the browser has applied pending style/layout work.
 *
 * Alpine’s `$nextTick` runs after DOM updates in the microtask queue, but often still in the same
 * frame as other directives. Reading `clientWidth`, `getBoundingClientRect()`, etc. there can force
 * synchronous layout. Prefer scheduling those reads for the next animation frame.
 *
 * **Convention (Alpine plugins):**
 * - After `$nextTick` (when the tree you care about is in the DOM), call `afterNextPaint` or
 *   `readLayoutAfterPaint` before measuring.
 * - After `Alpine.mutateDom` (or any synchronous DOM write), do not read layout in the same turn;
 *   use `readLayoutAfterPaint` (or a follow-up `requestAnimationFrame`) before measuring.
 * - For ongoing size changes, prefer `ResizeObserver` / `x-resize` and keep one-off “first measure”
 *   paths on this schedule.
 * - If deferred reads would leave stale geometry used by other synchronous logic (e.g. scroll
 *   thresholds derived from header heights), measure synchronously when registering — or refactor
 *   that logic so it does not run until after layout is known.
 */
function afterNextPaint(fn) {
    return requestAnimationFrame(fn);
}
function readLayoutAfterPaint(fn) {
    return new Promise((resolve) => {
        afterNextPaint(() => resolve(fn()));
    });
}

/**
 * Logs a clear, actionable console error when a Method UI directive is used
 * without a parent directive it depends on.
 *
 * Many directives only work when nested inside a parent directive that sets up
 * shared state on the Alpine scope (e.g. `x-overflow:start` needs an ancestor
 * `x-overflow`). When that parent is missing, the raw failure is an inscrutable
 * `this.__foo is not a function`. Call this instead so users get a message that
 * explains what to do.
 *
 * @param childDirective - The directive being used, e.g. `'x-overflow:start'`.
 * @param parentDirective - The parent directive required, e.g. `'x-overflow'`.
 * @param el - The element the child directive is on, logged for context.
 *
 * @example
 * ```ts
 * if (typeof this.__overflow_registerBoundary !== 'function') {
 *   warnMissingParentDirective(`x-overflow:${value}`, 'x-overflow', $el);
 *   return;
 * }
 * ```
 */
function warnMissingParentDirective(childDirective, parentDirective, el) {
    console.error(`Cannot use ${childDirective} without a parent element having ${parentDirective}.`, el);
}

const DEFAULT_SPEED = 60; // px/s
const DEFAULT_ATTRIBUTE_BLACKLIST = ['id'];
const SCROLL_PROGRESS_TO_MARQUEE_OFFSET_MULTIPLIER = 5;
const VIEWPORT_RESIZE_DEBOUNCE_MS = 100;
const SCROLL_SPRING_CONFIG = {
    stiffness: 100,
    damping: 40,
    mass: 0.2,
};
/**
 * This plugin provides a smoothly scrolling marquee effect by translating two matching rows:
 * the original "source" row and a cloned "target" row. The target starts immediately after the
 * source, so as the source scrolls away the target takes its place and the offset can wrap back
 * around without a visible jump.
 *
 * The render flow is:
 *  1. Measure the source and viewport.
 *  2. If `.fill` is enabled, append sanitized source clones until the source row is wide enough.
 *  3. Clone the finalized source row into the target row.
 *  4. Start or refresh the Motion animation that drives both rows from the same offset value.
 *
 * Inspired by: https://ryanmulligan.dev/blog/css-marquee/
 *
 * Modifiers:
 *  - speed.X: Adjusts the scrolling pace (e.g., speed.60 sets 60px per second). Negative value scrolls backwards
 *  - fill: Ensures content is repeated and appended until the visual area is completely covered.
 *  - pause: Begins the marquee in a paused state, requiring a play action to begin moving.
 *  - scroll: Advances the marquee incrementally as the page scroll position changes.
 *
 * General usage:
 *  1. Mark the scrolled content with x-marquee:source.
 *  2. Provide an empty container with x-marquee:target, to receive the duplicated set of items.
 *  3. Attach x-marquee to a parent element with optional modifiers like speed.X, fill, pause, or scroll.
 *
 * Programmatic control is available through $marquee, enabling methods like play, pause, or toggle
 * at any time.
 *
 * @example
 *   <div x-data x-marquee.speed.60.fill.scroll>
 *     <div x-marquee:source>
 *       <span>First Item</span>
 *       <span>Second Item</span>
 *       <span>Third Item</span>
 *     </div>
 *     <div x-marquee:target aria-hidden="true"></div>
 *   </div>
 *
 */
function marquee (Alpine) {
    Alpine.directive('marquee', ($el, { value, modifiers }) => {
        var _a;
        if (value == 'source') {
            // Child directives register the two moving rows on the parent x-data instance.
            Alpine.bind($el, {
                'x-init'() {
                    if (!this.__marquee_isRoot) {
                        warnMissingParentDirective('x-marquee:source', 'x-marquee', this.$el);
                        return;
                    }
                    this.__marquee_sourceEl = this.$el;
                },
            });
        }
        else if (value == 'target') {
            Alpine.bind($el, {
                'x-init'() {
                    if (!this.__marquee_isRoot) {
                        warnMissingParentDirective('x-marquee:target', 'x-marquee', this.$el);
                        return;
                    }
                    this.__marquee_targetEl = this.$el;
                },
            });
        }
        else if (value == null) {
            const speed = (_a = getNumberModifierValue(modifiers, 'speed')) !== null && _a !== void 0 ? _a : DEFAULT_SPEED;
            let fill = modifiers.includes('fill');
            let autoplay = !modifiers.includes('pause');
            let scrollWithPage = modifiers.includes('scroll');
            Alpine.bind($el, {
                'x-data'() {
                    return {
                        __marquee_isRoot: true,
                        __marquee_state: buildMarqueeState({ isPlaying: autoplay }),
                        __marquee_isInitialized: false,
                        __marquee_isActive: false,
                        __marquee_isRendering: false,
                        __marquee_wasPlayingBeforeKeyboardFocus: false,
                        __marquee_sourceEl: null,
                        __marquee_targetEl: null,
                        __marquee_originalContentFragment: document.createDocumentFragment(),
                        __marquee_originalContentWidth: 0,
                        __marquee_sourceElWidth: 0,
                        __marquee_viewportWidth: 0,
                        __marquee_offsetValue: motionValue(0),
                        __marquee_scrollOffsetValue: motionValue(0),
                        __marquee_scrollOffsetSpringValue: null,
                        __marquee_offsetTransformValue: null,
                        __marquee_animation: null,
                        __marquee_sourceElStyleEffect: null,
                        __marquee_targetElStyleEffect: null,
                        __marquee_cancelScroll: null,
                        __marquee_afterNextPaintId: null,
                        __marquee_afterNextPaintResolve: null,
                        __marquee_renderFrameId: null,
                        __marquee_resizeFrameId: null,
                        __marquee_resetRenderingFrameId: null,
                        __marquee_sentinel: null,
                        __marquee_cancelInView: null,
                        __marquee_measureWidths() {
                            // Reads are deferred until after paint so mutations from init/render have settled.
                            return readLayoutAfterPaint(() => {
                                this.__marquee_originalContentWidth = this.__marquee_sourceEl.clientWidth;
                                this.__marquee_sourceElWidth = this.__marquee_originalContentWidth;
                                this.__marquee_viewportWidth = this.$el.clientWidth;
                            });
                        },
                        get __marquee_animationDuration() {
                            return this.__marquee_sourceElWidth / Math.abs(speed);
                        },
                        /**
                         * Extends the source row itself for short content. The sentinel separates original
                         * content from fill clones so later renders can delete only generated nodes.
                         */
                        __marquee_fillSource() {
                            return (async () => {
                                if (this.__marquee_originalContentWidth === 0) {
                                    await readLayoutAfterPaint(() => {
                                        this.__marquee_originalContentWidth = this.__marquee_sourceEl.clientWidth;
                                        this.__marquee_sourceElWidth = this.__marquee_originalContentWidth;
                                    });
                                }
                                let needsWidthAfterDomChange = false;
                                Alpine.mutateDom(() => {
                                    if (!this.__marquee_sentinel) {
                                        // Everything after this marker is generated and can be replaced on resize.
                                        this.__marquee_sentinel = document.createComment('marquee-sentinel');
                                        this.__marquee_sourceEl.appendChild(this.__marquee_sentinel);
                                    }
                                    else {
                                        let next = this.__marquee_sentinel.nextSibling;
                                        while (next) {
                                            const toRemove = next;
                                            next = next.nextSibling;
                                            this.__marquee_sourceEl.removeChild(toRemove);
                                        }
                                        needsWidthAfterDomChange = true;
                                    }
                                });
                                if (needsWidthAfterDomChange) {
                                    await readLayoutAfterPaint(() => {
                                        this.__marquee_originalContentWidth = this.__marquee_sourceEl.clientWidth;
                                        this.__marquee_sourceElWidth = this.__marquee_originalContentWidth;
                                    });
                                }
                                const spaceToFill = Math.max(0, this.__marquee_viewportWidth - this.__marquee_originalContentWidth);
                                if (spaceToFill === 0)
                                    return;
                                const timesToClone = Math.ceil(spaceToFill / this.__marquee_originalContentWidth);
                                const fragment = document.createDocumentFragment();
                                for (let i = 0; i < timesToClone; i++) {
                                    const clone = this.__marquee_originalContentFragment.cloneNode(true);
                                    // Prepare detached clones once instead of re-scanning rendered nodes every render.
                                    Array.from(clone.children).forEach((child) => prepareClonedSourceElement(child));
                                    fragment.appendChild(clone);
                                }
                                await new Promise((resolve) => {
                                    Alpine.mutateDom(() => {
                                        this.__marquee_sentinel.after(fragment);
                                        Alpine.skipDuringClone(() => Alpine.initTree(this.__marquee_sourceEl))();
                                        this.__marquee_afterNextPaintResolve = resolve;
                                        this.__marquee_afterNextPaintId = afterNextPaint(() => {
                                            this.__marquee_afterNextPaintId = null;
                                            this.__marquee_afterNextPaintResolve = null;
                                            this.__marquee_sourceElWidth = this.__marquee_sourceEl.clientWidth;
                                            resolve();
                                        });
                                    });
                                });
                            })();
                        },
                        __marquee_cloneSourceToTarget() {
                            return new Promise((resolve) => {
                                // Without enough content to loop, the target would only repeat static content.
                                if (this.__marquee_sourceElWidth < this.__marquee_viewportWidth) {
                                    return resolve();
                                }
                                Alpine.mutateDom(() => {
                                    // Target is rebuilt from the finalized source so fill clones are included.
                                    const clone = this.__marquee_sourceEl.cloneNode(true);
                                    Array.from(clone.children).forEach((child) => prepareClonedTargetElement(child));
                                    this.__marquee_targetEl.replaceChildren(...clone.children);
                                    // Re-initialize alpine on cloned nodes
                                    Alpine.skipDuringClone(() => Alpine.initTree(this.__marquee_targetEl))();
                                    resolve();
                                });
                            });
                        },
                        __marquee_setupAnimations() {
                            var _a;
                            // The two rows share one wrapped percentage offset, so they stay perfectly locked.
                            const durationInSeconds = this.__marquee_animationDuration;
                            let animationStartOffset = this.__marquee_offsetValue.get();
                            if (this.__marquee_animation == null && speed < 0 && animationStartOffset === 0) {
                                animationStartOffset = -100;
                            }
                            const animationEndOffset = animationStartOffset + (speed < 0 ? 100 : -100);
                            const shouldPlay = this.__marquee_state.isPlaying && this.__marquee_sourceElWidth >= this.__marquee_viewportWidth;
                            (_a = this.__marquee_animation) === null || _a === void 0 ? void 0 : _a.stop();
                            this.__marquee_animation = animate(this.__marquee_offsetValue, [animationStartOffset, animationEndOffset], {
                                duration: durationInSeconds,
                                repeat: Infinity,
                                ease: 'linear',
                            });
                            if (!shouldPlay) {
                                // Keep the offset value ready even when there is not enough content to animate.
                                this.__marquee_animation.pause();
                            }
                            this.__marquee_enableScroll();
                        },
                        __marquee_enableScroll() {
                            if (!scrollWithPage)
                                return;
                            if (this.__marquee_cancelScroll != null)
                                return;
                            if (isReducedMotion() || isLowPowerDevice())
                                return;
                            if (this.__marquee_sourceElWidth < this.__marquee_viewportWidth)
                                return;
                            let lastScrollProgress = null;
                            // Page scroll nudges a spring that is added on top of the looping animation offset.
                            this.__marquee_cancelScroll = scroll((scrollProgress) => {
                                if (lastScrollProgress == null) {
                                    lastScrollProgress = scrollProgress;
                                    return;
                                }
                                const scrollProgressDelta = scrollProgress - lastScrollProgress;
                                lastScrollProgress = scrollProgress;
                                this.__marquee_nudgeOffsetByScrollProgress(scrollProgressDelta);
                            }, {
                                target: $el,
                                offset: ['end start', 'start end'],
                            });
                        },
                        __marquee_disableScroll() {
                            var _a;
                            (_a = this.__marquee_cancelScroll) === null || _a === void 0 ? void 0 : _a.call(this);
                            this.__marquee_cancelScroll = null;
                        },
                        __marquee_nudgeOffsetByScrollProgress(scrollProgressDelta) {
                            let offsetDirection = speed < 0 ? -1 : 1;
                            if (scrollProgressDelta > 0) {
                                offsetDirection = speed < 0 ? 1 : -1;
                            }
                            const newOffset = this.__marquee_scrollOffsetValue.get() +
                                scrollProgressDelta * SCROLL_PROGRESS_TO_MARQUEE_OFFSET_MULTIPLIER * offsetDirection;
                            this.__marquee_scrollOffsetValue.set(newOffset);
                        },
                        __marquee_scrollTo(element) {
                            if (!isFinite(this.__marquee_animationDuration))
                                return;
                            // Jump the animation timeline so keyboard focus lands near the visible left edge.
                            let offset = element.offsetLeft;
                            let leftSpacingBuffer = 24; // (px) To allow for the focus outline to be fully visible when tabbed through
                            // When the animation is reversed
                            if (speed < 0) {
                                offset = this.__marquee_sourceElWidth - offset;
                                leftSpacingBuffer = leftSpacingBuffer * -1;
                            }
                            const elementPositionPercentage = (offset - leftSpacingBuffer) / this.__marquee_sourceElWidth;
                            const elementPositionTime = elementPositionPercentage * this.__marquee_animationDuration;
                            if (isFinite(elementPositionTime) && this.__marquee_animation != null) {
                                this.__marquee_animation.time = elementPositionTime;
                            }
                        },
                        async __marquee_render() {
                            if (!this.__marquee_isActive)
                                return;
                            if (this.__marquee_isRendering)
                                return;
                            try {
                                this.__marquee_isRendering = true;
                                // Render is the only path that mutates generated marquee content.
                                await this.__marquee_measureWidths();
                                if (fill) {
                                    await this.__marquee_fillSource();
                                }
                                await this.__marquee_cloneSourceToTarget();
                                // Don't animate if in reduced motion mode
                                if (!isReducedMotion()) {
                                    // Setup animations after the content has been cloned in order to measure the width of the content element without causing reflow
                                    this.__marquee_setupAnimations();
                                }
                                this.__marquee_isInitialized = true;
                                this.__marquee_resetRenderingFrameId = window.requestAnimationFrame(() => {
                                    this.__marquee_resetRenderingFrameId = null;
                                    this.__marquee_isRendering = false;
                                });
                            }
                            finally {
                                this.__marquee_isRendering = false;
                            }
                        },
                        __marquee_updateViewportWidth() {
                            if (this.__marquee_resizeFrameId !== null) {
                                cancelAnimationFrame(this.__marquee_resizeFrameId);
                            }
                            this.__marquee_resizeFrameId = requestAnimationFrame(() => {
                                this.__marquee_resizeFrameId = null;
                                this.__marquee_viewportWidth = this.$el.clientWidth;
                                this.__marquee_render();
                            });
                        },
                        __marquee_pauseForKeyboardFocus(target) {
                            var _a;
                            if (!(target instanceof HTMLElement))
                                return;
                            // Preserve manual paused state: focusout should only resume if focus paused playback.
                            this.__marquee_wasPlayingBeforeKeyboardFocus = this.__marquee_state.isPlaying;
                            this.__marquee_state.pause();
                            (_a = this.__marquee_animation) === null || _a === void 0 ? void 0 : _a.pause();
                            this.__marquee_scrollTo(target);
                        },
                        __marquee_resumeAfterKeyboardFocus() {
                            var _a;
                            if (!this.__marquee_wasPlayingBeforeKeyboardFocus)
                                return;
                            this.__marquee_wasPlayingBeforeKeyboardFocus = false;
                            this.__marquee_state.play();
                            (_a = this.__marquee_animation) === null || _a === void 0 ? void 0 : _a.play();
                        },
                        __marquee_teardown() {
                            var _a;
                            this.__marquee_disableScroll();
                            (_a = this.__marquee_animation) === null || _a === void 0 ? void 0 : _a.stop();
                            this.__marquee_animation = null;
                            this.__marquee_isInitialized = false;
                        },
                        init() {
                            this.$nextTick(() => {
                                if (this.__marquee_sourceEl == null) {
                                    throw new Error('Source element not found, x-marquee:source must be applied to a child element of the x-marquee directive');
                                }
                                if (this.__marquee_targetEl == null) {
                                    throw new Error('Target element not found, x-marquee:target must be applied to a child element of the x-marquee directive');
                                }
                                this.__marquee_targetEl.setAttribute('aria-hidden', 'true');
                                // Keep a clean copy of the author-provided content; fill clones are generated from this.
                                Array.from(this.__marquee_sourceEl.children).forEach((element) => {
                                    const clone = element.cloneNode(true);
                                    for (const blacklistedAttribute of DEFAULT_ATTRIBUTE_BLACKLIST) {
                                        clone.removeAttribute(blacklistedAttribute);
                                    }
                                    this.__marquee_originalContentFragment.append(clone);
                                });
                                this.__marquee_scrollOffsetSpringValue = springValue(this.__marquee_scrollOffsetValue, SCROLL_SPRING_CONFIG);
                                this.__marquee_offsetTransformValue = transformValue(() => {
                                    // Motion values are composed here so scroll nudges do not disturb animation time.
                                    const wrappedOffsetValue = wrapOffsetPercentage(this.__marquee_offsetValue.get() + this.__marquee_scrollOffsetSpringValue.get());
                                    return `${wrappedOffsetValue}%`;
                                });
                                // Bind the looping marquee offset and scroll spring offset to both rendered elements.
                                this.__marquee_sourceElStyleEffect = styleEffect(this.__marquee_sourceEl, {
                                    x: this.__marquee_offsetTransformValue,
                                });
                                this.__marquee_targetElStyleEffect = styleEffect(this.__marquee_targetEl, {
                                    x: this.__marquee_offsetTransformValue,
                                });
                                // Rendering is lazy: do the expensive clone/measure work only while the marquee is visible.
                                this.__marquee_cancelInView = inView(this.$el, () => {
                                    this.__marquee_isActive = true;
                                    if (this.__marquee_renderFrameId !== null) {
                                        cancelAnimationFrame(this.__marquee_renderFrameId);
                                    }
                                    this.__marquee_renderFrameId = window.requestAnimationFrame(() => {
                                        this.__marquee_renderFrameId = null;
                                        if (!this.__marquee_isActive)
                                            return;
                                        this.__marquee_render();
                                    });
                                    return () => {
                                        if (this.__marquee_renderFrameId !== null) {
                                            cancelAnimationFrame(this.__marquee_renderFrameId);
                                            this.__marquee_renderFrameId = null;
                                        }
                                        this.__marquee_isActive = false;
                                        this.__marquee_teardown();
                                    };
                                });
                            });
                            this.$watch('__marquee_state.isPlaying', (playing) => {
                                var _a, _b;
                                if (playing) {
                                    (_a = this.__marquee_animation) === null || _a === void 0 ? void 0 : _a.play();
                                }
                                else {
                                    (_b = this.__marquee_animation) === null || _b === void 0 ? void 0 : _b.pause();
                                }
                            });
                        },
                        destroy() {
                            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
                            this.__marquee_disableScroll();
                            (_a = this.__marquee_cancelInView) === null || _a === void 0 ? void 0 : _a.call(this);
                            if (this.__marquee_afterNextPaintId !== null) {
                                cancelAnimationFrame(this.__marquee_afterNextPaintId);
                                this.__marquee_afterNextPaintId = null;
                            }
                            (_b = this.__marquee_afterNextPaintResolve) === null || _b === void 0 ? void 0 : _b.call(this);
                            this.__marquee_afterNextPaintResolve = null;
                            if (this.__marquee_renderFrameId !== null) {
                                cancelAnimationFrame(this.__marquee_renderFrameId);
                                this.__marquee_renderFrameId = null;
                            }
                            if (this.__marquee_resizeFrameId !== null) {
                                cancelAnimationFrame(this.__marquee_resizeFrameId);
                                this.__marquee_resizeFrameId = null;
                            }
                            if (this.__marquee_resetRenderingFrameId !== null) {
                                cancelAnimationFrame(this.__marquee_resetRenderingFrameId);
                                this.__marquee_resetRenderingFrameId = null;
                            }
                            (_c = this.__marquee_sourceElStyleEffect) === null || _c === void 0 ? void 0 : _c.call(this);
                            (_d = this.__marquee_targetElStyleEffect) === null || _d === void 0 ? void 0 : _d.call(this);
                            (_f = (_e = this.__marquee_offsetTransformValue) === null || _e === void 0 ? void 0 : _e.stop) === null || _f === void 0 ? void 0 : _f.call(_e);
                            this.__marquee_offsetTransformValue = null;
                            (_g = this.__marquee_sourceEl) === null || _g === void 0 ? void 0 : _g.replaceChildren(this.__marquee_originalContentFragment);
                            this.__marquee_originalContentFragment = document.createDocumentFragment();
                            (_h = this.__marquee_sentinel) === null || _h === void 0 ? void 0 : _h.remove();
                            (_j = this.__marquee_animation) === null || _j === void 0 ? void 0 : _j.stop();
                            (_l = (_k = this.__marquee_scrollOffsetSpringValue) === null || _k === void 0 ? void 0 : _k.stop) === null || _l === void 0 ? void 0 : _l.call(_k);
                            this.__marquee_animation = null;
                        },
                    };
                },
                '@focusin': '__marquee_pauseForKeyboardFocus($event.target)',
                '@focusout': '__marquee_resumeAfterKeyboardFocus()',
                'x-resize.document': Alpine.debounce(function () {
                    this.__marquee_updateViewportWidth();
                }, VIEWPORT_RESIZE_DEBOUNCE_MS),
            });
        }
        else {
            throw new Error(`Unknown directive: x-marquee:${value}`);
        }
    }).before('bind');
    Alpine.magic('marquee', ($el) => {
        const $data = Alpine.$data($el);
        return {
            get isPlaying() {
                var _a;
                return (_a = $data.__marquee_state) === null || _a === void 0 ? void 0 : _a.isPlaying;
            },
            play() {
                var _a;
                (_a = $data.__marquee_state) === null || _a === void 0 ? void 0 : _a.play();
            },
            pause() {
                var _a;
                (_a = $data.__marquee_state) === null || _a === void 0 ? void 0 : _a.pause();
            },
            toggle() {
                var _a;
                (_a = $data.__marquee_state) === null || _a === void 0 ? void 0 : _a.toggle();
            },
            scrollTo(element) {
                var _a;
                (_a = $data.__marquee_scrollTo) === null || _a === void 0 ? void 0 : _a.call($data, element);
            },
            get offsetValue() {
                return $data.__marquee_offsetValue;
            },
            get isReady() {
                return $data.__marquee_isInitialized;
            },
        };
    });
}
function buildMarqueeState(options) {
    var _a;
    return {
        isPlaying: (_a = options === null || options === void 0 ? void 0 : options.isPlaying) !== null && _a !== void 0 ? _a : false,
        play() {
            this.isPlaying = true;
        },
        pause() {
            this.isPlaying = false;
        },
        toggle() {
            this.isPlaying = !this.isPlaying;
        },
    };
}
function wrapOffsetPercentage(offsetPercentage) {
    const wrappedOffsetPercentage = ((offsetPercentage % 100) + 100) % 100;
    return wrappedOffsetPercentage === 0 ? 0 : wrappedOffsetPercentage - 100;
}
function prepareClonedSourceElement(element) {
    // Source fill clones remain in the visible row, but should not be announced or tabbed to.
    element.setAttribute('aria-hidden', 'true');
    removeClonedElementFromTabOrder(element);
}
function prepareClonedTargetElement(element) {
    // Target clones are hidden by the target container; this only keeps keyboard tab order clean.
    removeClonedElementFromTabOrder(element);
}
function removeClonedElementFromTabOrder(element) {
    // Clones are detached when prepared, so tabbable cannot rely on layout visibility checks.
    if (isFocusable(element, { displayCheck: 'none' })) {
        element.setAttribute('tabindex', '-1');
    }
    focusable(element, { displayCheck: 'none' }).forEach((focusableElement) => {
        focusableElement.setAttribute('tabindex', '-1');
    });
}

/**
 * x-marquee-track — couples the parent marquee's position to the visitor's scroll
 * direction *exactly* while scrolling, then coasts back to the loop's cruising
 * speed when scrolling stops.
 *
 * A rAF loop runs from the first scroll event until handoff. Each frame is one of:
 *   - Event frame: a scroll event fired since the last tick. The event already set
 *     the offset 1:1 with the scrollbar (exact tracking), so the tick leaves it be.
 *   - Coast frame: no event fired. The tick integrates the tracked velocity into
 *     the offset, so motion never freezes between events — or after the gesture.
 *
 * Once the page has been still for SCROLL_SILENCE_MS, coast frames also *relax*
 * the velocity toward the loop's cruising velocity along an exponential curve.
 * When the two velocities meet we call `__marquee_setupAnimations()`, which
 * resumes the native loop from the current offset — seamless in both position and
 * speed.
 *
 * The earlier version handed back the instant scrolling stopped, snapping from
 * scroll-speed to loop-speed (a "cold stop"); a later attempt froze the offset for
 * the whole silence window. Coasting per-frame removes both: the marquee carries
 * its momentum out of the gesture and eases down, the way a flywheel would.
 *
 * Leans on Motion primitives the marquee plugin already exposes: `offsetValue` is
 * a Motion `motionValue` (so `.getVelocity()` is available) and
 * `__marquee_animationDuration` lets us derive cruising velocity (100 offset units
 * per duration).
 */
// === TUNING CONSTANTS ===========================================================
// Offset units (the loop's 0→-100 range) applied per pixel scrolled. Governs how
// tightly position tracks the scrollbar during the tracking phase.
const SCROLL_TO_OFFSET = 0.05;
// Stillness after the last scroll event before the velocity starts easing back to
// cruising speed. Also the tolerance for a momentary mid-gesture pause: shorter
// pauses keep coasting at scroll speed rather than decaying.
const SCROLL_SILENCE_MS = 80;
// Velocity-relaxation time constant (seconds). Larger = a longer, gentler coast
// back to cruising speed. This single value sets how the bridge "feels".
const BRIDGE_TAU = 0.4;
// Hand back to the native loop once within this fraction of cruising speed. Small
// enough that the velocity splice at handoff is imperceptible.
const BRIDGE_SETTLE_FRACTION = 0.04;
// Cap the velocity carried into the decay to this multiple of cruising speed, so a
// hard flick produces a bounded coast rather than a multi-second drift. Applied
// only when decay begins, so it never throttles exact tracking mid-gesture.
const MAX_BRIDGE_VELOCITY_FACTOR = 8;
// EMA weight on the newest scroll-velocity sample (0..1). Tames jitter from bursty
// scroll events feeding the coast velocity.
const SCROLL_VELOCITY_SMOOTHING = 0.4;
// Clamp a single integration step (e.g. after a tab refocus) so a long idle frame
// can't fling the offset.
const MAX_FRAME_DT = 1 / 30;
const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
function marqueeScrollTrack(Alpine) {
    Alpine.directive('marquee-track', ($el, _data, { cleanup }) => {
        // The marquee itself doesn't animate under reduced motion; don't introduce
        // scroll-driven movement the visitor opted out of.
        if (prefersReducedMotion())
            return;
        let lastScrollY = window.scrollY;
        let lastScrollTime = 0; // performance.now() of the last scroll event
        let lastTickTime = 0; // performance.now() of the last rAF tick
        let rafId = null;
        let isTracking = false; // native loop stopped; we own the offset
        let eventThisFrame = false; // a scroll event set the offset since the last tick
        let velocity = 0; // current offset velocity (units/sec)
        let loopVelocity = 0; // native cruising velocity (units/sec, signed)
        let detachedAnimation = null; // the native loop we've stopped
        // Travel direction from the rendered `x-marquee.speed.N.fill` modifier (the
        // declarative source of truth). Negative speed → reverse the scroll drive so
        // paired forward/reverse marquees diverge as the page scrolls. Default forward.
        const speedAttr = $el.getAttributeNames().find((n) => n.includes('x-marquee.speed.'));
        const speedMatch = speedAttr?.match(/\.speed\.(-?\d+)/);
        const speedSign = speedMatch && Number(speedMatch[1]) < 0 ? -1 : 1;
        const getData = () => Alpine.$data($el);
        // The marquee re-creates and *plays* its native loop on every inView/resize
        // render — including the one fired when it scrolls into view. While we own the
        // offset that fresh loop would clobber our scroll-driven position, so detach
        // (stop) any animation we haven't already stopped.
        const detachNativeLoop = (data) => {
            const anim = data.__marquee_animation;
            if (anim && anim !== detachedAnimation) {
                anim.stop();
                detachedAnimation = anim;
            }
        };
        // Cruising velocity: prefer Motion's live reading (correct sign + magnitude
        // while the loop is running); fall back to deriving it from the duration.
        const measureLoopVelocity = (data) => {
            const observed = data.__marquee_offsetValue.getVelocity?.() ?? 0;
            if (Math.abs(observed) > 0.001)
                return observed;
            const duration = data.__marquee_animationDuration;
            // Forward loops travel offset 0→-100 (velocity −); reverse loops -100→0 (+).
            if (duration > 0 && isFinite(duration))
                return (-speedSign * 100) / duration;
            return 0;
        };
        const handoff = (data) => {
            isTracking = false;
            velocity = 0;
            detachedAnimation = null; // the loop setupAnimations creates is the one we want running
            // Reads the current offset as the new start. Because the coast has already
            // matched cruising velocity, the splice is seamless in position and speed.
            data.__marquee_setupAnimations();
        };
        const tick = (now) => {
            rafId = null;
            const data = getData();
            if (!data?.__marquee_offsetValue)
                return;
            detachNativeLoop(data); // a render may have re-armed the loop since last frame
            const dt = Math.min((now - lastTickTime) / 1000, MAX_FRAME_DT);
            lastTickTime = now;
            const sinceScroll = now - lastScrollTime;
            // After the page falls silent, ease the velocity toward cruising speed.
            if (sinceScroll >= SCROLL_SILENCE_MS) {
                const cap = Math.abs(loopVelocity) * MAX_BRIDGE_VELOCITY_FACTOR;
                if (cap > 0)
                    velocity = Math.max(-cap, Math.min(cap, velocity));
                velocity += (loopVelocity - velocity) * (1 - Math.exp(-dt / BRIDGE_TAU));
            }
            if (eventThisFrame) {
                // A scroll event already placed the offset this frame; don't coast on top.
                eventThisFrame = false;
            }
            else {
                // Coast: carry momentum so motion never freezes between events or after
                // the gesture ends.
                data.__marquee_offsetValue.set(data.__marquee_offsetValue.get() + velocity * dt);
            }
            const settleEpsilon = Math.max(0.25, Math.abs(loopVelocity) * BRIDGE_SETTLE_FRACTION);
            if (sinceScroll >= SCROLL_SILENCE_MS && Math.abs(velocity - loopVelocity) <= settleEpsilon) {
                handoff(data);
                return;
            }
            rafId = requestAnimationFrame(tick);
        };
        const handleScroll = () => {
            const data = getData();
            if (!data?.__marquee_offsetValue)
                return; // marquee x-data not ready yet
            const now = performance.now();
            const currY = window.scrollY;
            const dy = currY - lastScrollY;
            // Keep this fresh even before tracking engages, so the first tracked delta is
            // the latest frame's movement — never a stale jump accumulated since page load.
            lastScrollY = currY;
            if (dy === 0)
                return;
            // Not initialized yet (lazy render pending). If the marquee is near the
            // viewport, jumpstart its render so tracking engages as it scrolls in rather
            // than waiting on the IntersectionObserver and missing the gesture.
            if (!data.__marquee_animation || !data.__marquee_setupAnimations) {
                if (data.__marquee_sourceEl &&
                    !data.__marquee_isRendering &&
                    typeof data.__marquee_render === 'function' &&
                    $el.getBoundingClientRect().top < window.innerHeight * 1.5) {
                    data.__marquee_isActive = true;
                    data.__marquee_render();
                }
                return;
            }
            if (!isTracking) {
                // Capture cruising velocity while the loop still runs, then detach it.
                loopVelocity = measureLoopVelocity(data);
                velocity = loopVelocity;
                data.__marquee_animation.stop();
                detachedAnimation = data.__marquee_animation;
                isTracking = true;
                lastScrollTime = now;
                lastTickTime = now;
            }
            else {
                // A render between ticks may have re-armed the loop; keep it detached.
                detachNativeLoop(data);
            }
            // Exact 1:1 tracking: drive the offset straight from the scroll delta, signed
            // by the marquee's travel direction so reverse-speed marquees track oppositely.
            const offsetDelta = -dy * SCROLL_TO_OFFSET * speedSign;
            data.__marquee_offsetValue.set(data.__marquee_offsetValue.get() + offsetDelta);
            eventThisFrame = true;
            // Track scroll-driven velocity (smoothed) so the coast continues exactly
            // where the gesture left off — no freeze, no snap.
            const dtScroll = (now - lastScrollTime) / 1000;
            if (dtScroll > 0) {
                const instantaneous = offsetDelta / dtScroll;
                velocity += (instantaneous - velocity) * SCROLL_VELOCITY_SMOOTHING;
            }
            lastScrollTime = now;
            if (rafId == null) {
                lastTickTime = now;
                rafId = requestAnimationFrame(tick);
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        cleanup(() => {
            window.removeEventListener('scroll', handleScroll);
            if (rafId != null)
                cancelAnimationFrame(rafId);
            // Re-arm the loop so we don't leave a stopped marquee behind.
            if (isTracking) {
                const data = getData();
                data?.__marquee_setupAnimations?.();
            }
        });
    });
}

Alpine.plugin(marquee);
Alpine.plugin(marqueeScrollTrack);
